// Barra de navegação inferior para mobile — contextual por role
import { Box, Flex, Icon, Text, useColorModeValue } from '@chakra-ui/react';
import { NavLink, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { MdPersonOutline } from 'react-icons/md';
import { useAuth } from 'views/admin/auth/AuthProvider';
import routes from 'routes';

export default function BottomTabBar() {
  const location = useLocation();
  const { role, user, ready } = useAuth() || {};
  // normaliza para minúsculo, tolerando inconsistências de dados no Firestore
  const normalizedRole = role?.toLowerCase?.() ?? null;

  const bg           = useColorModeValue('white', 'navy.800');
  const borderColor  = useColorModeValue('gray.100', 'navy.700');
  const activeColor  = useColorModeValue('brand.500', 'brand.200');
  const inactiveColor = useColorModeValue('gray.400', 'whiteAlpha.500');

  const canSee = (route) => {
    if (route.invisible) return false;
    if (!route.roles || route.roles.length === 0) return false;
    return normalizedRole && route.roles.map(r => r.toLowerCase()).includes(normalizedRole);
  };

  // Pega só rotas diretas (não collapse), visíveis para o role atual, máximo 4 tabs
  const tabRoutes = routes
    .filter((r) => !r.collapse && !r.invisible && canSee(r))
    .slice(0, 4);

  // Tab de Perfil para todos os roles autenticados
  const perfilTab = user?.uid
    ? { name: 'Perfil', fullPath: `/admin/main/users/settings/${user.uid}`, icon: <Icon as={MdPersonOutline} width="20px" height="20px" color="inherit" /> }
    : null;

  // Não renderiza até auth estar pronto, ou se não há tabs para o role atual
  if (!ready || tabRoutes.length === 0) return null;

  const bar = (
    <Flex
      display={{ base: 'flex', xl: 'none' }}
      position="fixed"
      bottom="0"
      left="0"
      right="0"
      zIndex="1000"
      bg={bg}
      borderTop="1px solid"
      borderColor={borderColor}
      h="60px"
      align="stretch"
      boxShadow="0 -2px 10px rgba(0,0,0,0.07)"
    >
      {[...tabRoutes, ...(perfilTab ? [perfilTab] : [])].map((route, i) => {
        const to = route.fullPath ?? `${route.layout}${route.path}`;
        const isActive = location.pathname === to || location.pathname.startsWith(to + '/');
        const label =
          route.name.length > 12
            ? route.name.slice(0, 11) + '…'
            : route.name;

        return (
          <NavLink key={i} to={to} style={{ flex: 1, textDecoration: 'none' }}>
            <Flex
              direction="column"
              align="center"
              justify="center"
              h="100%"
              px="4px"
              color={isActive ? activeColor : inactiveColor}
              position="relative"
              transition="color 0.15s"
            >
              {/* Linha ativa no topo */}
              {isActive && (
                <Box
                  position="absolute"
                  top="0"
                  left="20%"
                  right="20%"
                  h="2px"
                  bg="brand.500"
                  borderBottomRadius="2px"
                />
              )}

              {/* Ícone — herda color do Flex pai via CSS */}
              <Box
                fontSize="20px"
                lineHeight="1"
                mb="2px"
                opacity={isActive ? 1 : 0.7}
              >
                {route.icon}
              </Box>

              <Text
                fontSize="9px"
                fontWeight={isActive ? '700' : '400'}
                lineHeight="1"
                textAlign="center"
                noOfLines={1}
              >
                {label}
              </Text>
            </Flex>
          </NavLink>
        );
      })}
    </Flex>
  );

  return createPortal(bar, document.body);
}
